import { Heart, MessageCircle, Share2, Instagram, Twitter, Facebook } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';

const liveFeedPosts = [
  {
    id: 1,
    author: '@AdventureWithSarah',
    avatar: 'https://images.unsplash.com/photo-1768347441028-032fd139bd56?w=100',
    platform: 'instagram',
    time: '5 min ago',
    text: 'Day 3 of the Appalachian Trail and my Moab 3s are holding up PERFECTLY! No break-in period needed, just pure comfort from mile one. 🥾⛰️ #MerrellMoab #AppalachianTrail #HikingGear',
    image: 'https://images.unsplash.com/photo-1578592391689-0e3d1a1b52b9?w=600',
    likes: 2847,
    comments: 234,
    shares: 89,
    sentiment: 'positive'
  },
  {
    id: 2,
    author: '@TrailRunnerJake',
    avatar: 'https://images.unsplash.com/photo-1762709753300-342ab94e8b05?w=100',
    platform: 'twitter',
    time: '18 min ago',
    text: 'Just PR\'d my favorite trail run in the Agility Peak 5! The grip on those rocky descents is INSANE. @Merrell knows what runners need 🏃‍♂️💨 #TrailRunning #MerrellRunning',
    likes: 1563,
    comments: 127,
    shares: 45,
    sentiment: 'positive'
  },
  {
    id: 3,
    author: '@OutdoorLifeMike',
    avatar: 'https://images.unsplash.com/photo-1663550369129-b25db8cacaf2?w=100',
    platform: 'instagram',
    time: '42 min ago',
    text: 'Tested 15 different hiking boots this season. The Moab 3 consistently outperformed in waterproofing, comfort, and durability. Full review dropping tomorrow! 📸🥾',
    image: 'https://images.unsplash.com/photo-1759040655848-60ac4a7906b4?w=600',
    likes: 4521,
    comments: 567,
    shares: 234,
    sentiment: 'positive'
  },
  {
    id: 4,
    author: '@BackpackingBeth',
    avatar: 'https://images.unsplash.com/photo-1548256921-d8a1f19bd52c?w=100',
    platform: 'youtube',
    time: '1h ago',
    text: 'NEW VIDEO: 7-day backcountry trip gear review! My Merrell boots were the MVP. Link in bio 🎥⛺',
    image: 'https://images.unsplash.com/photo-1519144674309-fcb7fbc1a054?w=600',
    likes: 8934,
    comments: 892,
    shares: 445,
    sentiment: 'positive'
  },
  {
    id: 5,
    author: '@NaturePhotoDan',
    avatar: 'https://images.unsplash.com/photo-1682695795798-1b31ea040caf?w=100',
    platform: 'instagram',
    time: '2h ago',
    text: 'Chasing waterfalls in the PNW. These Moab Speed 2s are perfect for fast-paced photography hikes! Light, grippy, and ready for anything 📷🌲 @Merrell #OutdoorPhotography',
    image: 'https://images.unsplash.com/photo-1648910172890-50980d434d60?w=600',
    likes: 3214,
    comments: 189,
    shares: 76,
    sentiment: 'positive'
  },
  {
    id: 6,
    author: '@UltraRunnerEmily',
    avatar: 'https://images.unsplash.com/photo-1768347441028-032fd139bd56?w=100',
    platform: 'twitter',
    time: '3h ago',
    text: 'Mile 47 of 100. Still no hot spots. The Agility Peak 5 is the real deal for ultras. Thank you @Merrell! 🏃‍♀️💪 #UltraRunning #100Miler',
    likes: 2156,
    comments: 298,
    shares: 112,
    sentiment: 'positive'
  },
  {
    id: 7,
    author: '@GearTestHQ',
    avatar: 'https://images.unsplash.com/photo-1663550369129-b25db8cacaf2?w=100',
    platform: 'instagram',
    time: '4h ago',
    text: 'After 6 months and 300+ miles, here\'s our honest review of the Merrell Moab 3: Still looks new, zero issues, and the comfort hasn\'t degraded one bit. Editor\'s Choice ⭐',
    likes: 5789,
    comments: 423,
    shares: 189,
    sentiment: 'positive'
  },
  {
    id: 8,
    author: '@AdventureCouple',
    avatar: 'https://images.unsplash.com/photo-1548256921-d8a1f19bd52c?w=100',
    platform: 'facebook',
    time: '5h ago',
    text: 'Just completed our first thru-hike! 500 miles in Merrell boots. They carried us through every terrain imaginable. Forever grateful 🥾❤️',
    likes: 1876,
    comments: 234,
    shares: 67,
    sentiment: 'positive'
  }
];

const getPlatformIcon = (platform: string) => {
  switch (platform) {
    case 'instagram':
      return <Instagram className="w-4 h-4" style={{ color: '#e4405f' }} />;
    case 'twitter':
      return <Twitter className="w-4 h-4" style={{ color: '#1da1f2' }} />;
    case 'facebook':
      return <Facebook className="w-4 h-4" style={{ color: '#1877f2' }} />;
    default:
      return null;
  }
};

export function BrandPulseLiveFeed() {
  return (
    <div className="space-y-4">
      {liveFeedPosts.map((post) => (
        <Card key={post.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                <ImageWithFallback
                  src={post.avatar}
                  alt={post.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900 font-medium">{post.author}</span>
                    <Badge className="bg-green-100 text-green-700 border-0 text-xs">
                      {post.sentiment}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPlatformIcon(post.platform)}
                    <span className="text-xs text-gray-400">{post.time}</span>
                  </div>
                </div>
                <p className="text-gray-700 mb-3">{post.text}</p>
                {post.image && (
                  <div className="rounded-lg overflow-hidden mb-3 bg-gray-100">
                    <ImageWithFallback
                      src={post.image}
                      alt="Post image"
                      className="w-full h-64 object-cover"
                    />
                  </div>
                )}
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <button className="flex items-center gap-1.5 hover:text-red-500 transition-colors">
                    <Heart className="w-4 h-4" />
                    <span>{post.likes.toLocaleString()}</span>
                  </button>
                  <button className="flex items-center gap-1.5 hover:text-purple-600 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                    <span>{post.comments}</span>
                  </button>
                  <button className="flex items-center gap-1.5 hover:text-purple-600 transition-colors">
                    <Share2 className="w-4 h-4" />
                    <span>{post.shares}</span>
                  </button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
